// ============================================
// Withdraw Page - New Style
// ============================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaMoneyBillWave, FaHome, FaChartBar, FaEye, FaFileAlt, FaChevronRight, FaTimes, FaInfoCircle, FaExclamationCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import config from '../config.json';
import './Transaction.css';

const Withdraw = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: เลือกเครือข่าย, 2: เลือกประเภท, 3: ฟอร์มถอนเงิน
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [selectedType, setSelectedType] = useState(''); // 'crypto' หรือ 'bank'
  const [selectedBank, setSelectedBank] = useState('');
  const [showBankModal, setShowBankModal] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USDT');
  const [network, setNetwork] = useState('BEP20');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const availableNetworksList = config.withdrawal?.networks || ['TRC20', 'ERC20', 'BEP20', 'Polygon'];

  const banks = [
    { id: 'bbl', name: 'ธนาคารกรุงเทพ', code: 'BBL', color: '#1e3a8a', image: '/images/assets/BBL.png' },
    { id: 'kbank', name: 'ธนาคารกสิกรไทย', code: 'KBANK', color: '#0066cc', image: '/images/assets/KBANK.png' },
    { id: 'scb', name: 'ธนาคารไทยพาณิชย์', code: 'SCB', color: '#4e2a7e', image: '/images/assets/SCB.png' },
    { id: 'ktb', name: 'ธนาคารกรุงไทย', code: 'KTB', color: '#1a5490', image: '/images/assets/KTB.png' },
    { id: 'ttb', name: 'ธนาคารทหารไทยธนชาต', code: 'TTB', color: '#00529b', image: '/images/assets/TTB.png' },
    { id: 'krungsri', name: 'ธนาคารกรุงศรีอยุธยา', code: 'BAY', color: '#ffd100', image: '/images/assets/BAY.png' },
    { id: 'gsb', name: 'ธนาคารออมสิน', code: 'GSB', color: '#e60012', image: '/images/assets/GSB.png' },
  ];

  const handleNetworkSelect = (network) => {
    if (step === 1) {
      // ถ้าอยู่ Step 1 ให้เปิด modal เลือกประเภท
      setSelectedNetwork(network);
      setShowNetworkModal(true);
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setShowNetworkModal(false);
    
    if (type === 'crypto') {
      // ถ้าเลือก Tether BEP20 - ใช้ BEP20 แบบเดียว
      setNetwork('BEP20');
      setSelectedNetwork('BEP20');
      setCurrency('USDT');
      setStep(3); // ไปหน้า form ถอนเงิน
    } else if (type === 'bank') {
      // ถ้าเลือก Bank transfer
      setCurrency('CASH');
      setNetwork('Bank');
      setStep(3); // ไปหน้า form ถอนเงิน
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const withdrawAmount = parseFloat(amount);
    if (!withdrawAmount || withdrawAmount < config.withdrawal.minAmount) {
      toast.error(`จำนวนเงินขั้นต่ำคือ $${config.withdrawal.minAmount}`);
      return;
    }

    if (withdrawAmount > user.balance) {
      toast.error('ยอดเงินไม่เพียงพอ');
      return;
    }

    if (selectedType === 'bank') {
      if (!firstName.trim()) {
        toast.error('กรุณากรอกชื่อ');
        return;
      }
      if (!lastName.trim()) {
        toast.error('กรุณากรอกนามสกุล');
        return;
      }
      if (!address.trim()) {
        toast.error('กรุณากรอกหมายเลขบัญชีธนาคาร');
        return;
      }
      if (!selectedBank) {
        toast.error('กรุณาเลือกธนาคาร');
        return;
      }
    } else if (selectedType === 'crypto') {
      if (!address.trim()) {
        toast.error('กรุณากรอกที่อยู่กระเป๋าเงิน');
        return;
      }
    }

    setLoading(true);
    try {
      // สำหรับ Bank transfer ให้ส่งข้อมูลเพิ่มเติม
      // สำหรับ Crypto ให้ส่งแค่ที่อยู่กระเป๋าเงิน
      if (selectedType === 'bank') {
        const selectedBankData = banks.find(b => b.id === selectedBank);
        await api.requestWithdraw(
          withdrawAmount, 
          currency, 
          network, 
          address,
          firstName,
          lastName,
          selectedBankData?.name || ''
        );
      } else {
        await api.requestWithdraw(withdrawAmount, currency, network, address);
      }
      toast.success('ส่งคำขอถอนเงินสำเร็จ กรุณารอแอดมินตรวจสอบ');
      navigate('/profile');
    } catch (error) {
      toast.error(error.message || 'ไม่สามารถส่งคำขอได้');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="transaction-page">
      {/* Header */}
      <div className="transaction-header">
        <button className="btn-back" onClick={() => navigate('/profile')}>
          <FaArrowLeft />
        </button>
        <h1>
          <FaMoneyBillWave /> ถอนเงิน
        </h1>
      </div>

      <div className="transaction-content">
        <div className="transaction-card">
          {/* Step 1: เลือกประเภท */}
          {step === 1 && (
            <>
              <div className="info-box info">
                <p>กรุณาเลือกประเภทการถอนเงิน</p>
              </div>
              <div className="form-group">
                <label>เลือกประเภทการถอน</label>
                <div 
                  className="network-select-field"
                  onClick={() => setShowNetworkModal(true)}
                >
                  <span>เลือกประเภทการถอนเงิน</span>
                  <FaChevronRight className="chevron-icon" />
                </div>
              </div>
            </>
          )}

          {/* Step 3: ฟอร์มถอนเงิน */}
          {step === 3 && (
            <>

              <form onSubmit={handleSubmit} className="transaction-form">
                {selectedType === 'crypto' && (
                  <>
                    <div className="form-group">
                      <label>สกุลเงิน</label>
                      <input
                        type="text"
                        className="input"
                        value="USDT (Tether)"
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label>ที่อยู่กระเป๋าเงิน (BEP20)</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="กรอกที่อยู่กระเป๋าเงิน BEP20"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={loading}
                        required
                      />
                    </div>
                  </>
                )}

                {selectedType === 'bank' && (
                  <>
                    <div className="form-group">
                      <label>เครือข่าย</label>
                      <input
                        type="text"
                        className="input"
                        value="Bank transfer"
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label>จำนวนเงิน</label>
                      <input
                        type="number"
                        className="input"
                        placeholder={`ขั้นต่ำ $${config.withdrawal.minAmount}`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={loading}
                        min={config.withdrawal.minAmount}
                        step="0.01"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>ชื่อธนาคาร</label>
                      <div className="bank-name-input-wrapper">
                        <div className="bank-name-display" onClick={() => setShowBankModal(true)}>
                          {selectedBank ? (
                            <>
                              <img 
                                src={banks.find(b => b.id === selectedBank)?.image || '/images/assets/default.svg'} 
                                alt={banks.find(b => b.id === selectedBank)?.name}
                                className="bank-logo-small"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <span>{banks.find(b => b.id === selectedBank)?.name}</span>
                            </>
                          ) : (
                            <span>กรุณาเลือกธนาคาร</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="bank-select-btn"
                          onClick={() => setShowBankModal(true)}
                          disabled={loading}
                        >
                          <FaExclamationCircle />
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>ชื่อ</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="กรอกชื่อ"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={loading || !selectedBank}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>นามสกุล</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="กรอกนามสกุล"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={loading || !selectedBank}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>หมายเลขบัญชี</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="กรอกหมายเลขบัญชี"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={loading || !selectedBank}
                        required
                      />
                    </div>
                  </>
                )}

                {selectedType !== 'bank' && (
                  <div className="form-group">
                    <label>จำนวนเงิน (USD)</label>
                    <input
                      type="number"
                      className="input"
                      placeholder={`ขั้นต่ำ $${config.withdrawal.minAmount}`}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={loading}
                      min={config.withdrawal.minAmount}
                      step="0.01"
                      required
                    />
                  </div>
                )}
                )}

                <button
                  type="submit"
                  className="btn btn-warning btn-block"
                  disabled={loading || (selectedType === 'bank' && !selectedBank)}
                >
                  {loading ? 'กำลังส่งคำขอ...' : 'ถอนเงิน'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Network Type Selection Modal */}
      {showNetworkModal && (
        <div className="network-modal-overlay" onClick={() => setShowNetworkModal(false)}>
          <div className="network-modal" onClick={(e) => e.stopPropagation()}>
            <div className="network-modal-header">
              <h2>เลือกเครือข่ายสาธารณะ</h2>
              <button 
                className="network-modal-close"
                onClick={() => setShowNetworkModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="network-modal-warning">
              <p>กรุณาเลือกเครือข่ายสาธารณะ</p>
              <p>กรุณาตรวจสอบเครือข่ายให้แน่ใจว่าเครือข่ายทั้งสองตรงกันก่อนที่จะทำการฝากสินทรัพย์ทุกครั้ง หากคุณเลือกเครือข่ายผิด สินทรัพย์อาจสูญหายได้</p>
            </div>

            <div className="network-type-options">
              <div 
                className="network-type-option"
                onClick={() => handleTypeSelect('crypto')}
              >
                <div className="network-type-radio">
                  <input 
                    type="radio" 
                    name="networkType" 
                    checked={selectedType === 'crypto'}
                    readOnly
                  />
                </div>
                <div className="network-type-info">
                  <div className="network-type-name">Tether (BEP20)</div>
                  <div className="network-type-currency">USDT</div>
                </div>
                <div className="network-type-fee">
                  <span>ค่าธรรมเนียม</span>
                  <span>= USD</span>
                </div>
              </div>

              <div 
                className="network-type-option"
                onClick={() => handleTypeSelect('bank')}
              >
                <div className="network-type-radio">
                  <input 
                    type="radio" 
                    name="networkType" 
                    checked={selectedType === 'bank'}
                    readOnly
                  />
                </div>
                <div className="network-type-info">
                  <div className="network-type-name">Bank transfer</div>
                  <div className="network-type-currency">CASH</div>
                </div>
                <div className="network-type-fee">
                  <span>ค่าธรรมเนียม</span>
                  <span>= USD</span>
                </div>
              </div>
            </div>

            <div className="network-modal-footer">
              <FaInfoCircle /> ข้อมูลเพิ่มเติม
            </div>
          </div>
        </div>
      )}

      {/* Bank Selection Modal */}
      {showBankModal && (
        <div className="network-modal-overlay" onClick={() => setShowBankModal(false)}>
          <div className="network-modal" onClick={(e) => e.stopPropagation()}>
            <div className="network-modal-header">
              <h2>เลือกธนาคาร</h2>
              <button 
                className="network-modal-close"
                onClick={() => setShowBankModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="bank-modal-content">
              <div className="bank-modal-grid">
                {banks.map((bank) => (
                    <button
                      key={bank.id}
                      type="button"
                      className={`bank-modal-card ${selectedBank === bank.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedBank(bank.id);
                        setNetwork(bank.code || bank.id.toUpperCase());
                        setShowBankModal(false);
                      }}
                      style={{ '--bank-color': bank.color }}
                    >
                      <div className="bank-modal-icon">
                        <img 
                          src={bank.image || '/images/assets/default.svg'} 
                          alt={bank.name}
                          className="bank-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.parentElement;
                            if (fallback) {
                              fallback.innerHTML = `<span>${bank.code || bank.id.substring(0, 2).toUpperCase()}</span>`;
                              fallback.style.backgroundColor = bank.color;
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                      </div>
                      <div className="bank-modal-name">{bank.name}</div>
                    </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default Withdraw;
